/**
 * RunsActivityCard — a workflow's runs per day, as the RANGE SELECTOR for the
 * Runs tab. Promoted out of the workflow-detail design prototype so the product
 * page and the prototype render one implementation (the parity audit's §6: the
 * product had no activity surface at all, though the endpoint has always
 * existed).
 *
 * The selection lives in the URL (`?from`/`?to`) — the SAME params the runs
 * table filters on — so the chart and the table can never disagree, the state
 * survives a reload, and a review can deep-link a day. A single selected day is
 * a one-day range (from === to).
 */

import * as React from "react";

import { Skeleton } from "../../skeleton";
import { cn } from "../../utils";
import { formatDayMonth, formatNumber, formatUsd } from "../../format";
import { useApi } from "../../api/client";
import {
  workflowRunsByDay,
  type RunsByDayRowDto,
} from "../../api/contracts/workflow-runs";
import { SectionCard } from "../section-card";
import { GraphTip } from "../graph-tip";

export function RunsActivityCard({
  loopId,
  days: rangeDays,
  selectedDay,
  onSelectDay,
}: {
  loopId: number;
  /** Trailing window from the page's report range; null = all time. */
  days: number | null;
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
}) {
  // The chart's window is the header range, NOT the table's date filter — so it
  // fetches the loop's per-day series for the whole range and stays put when you
  // click a day to filter the table below.
  const { data, isLoading } = useApi(workflowRunsByDay, {
    query: { loop_id: loopId, ...(rangeDays != null ? { days: rangeDays } : {}) },
  });
  const days = data?.rows ?? [];
  // ONE ceiling for the whole plot: total runs is always the largest quantity on
  // a day (completed + failed + running sum to it), so scaling every mark by the
  // busiest day's TOTAL keeps each colored line INSIDE that day's gray "total
  // runs" bar — the gap from a bar's top down to the green completed line reads
  // as the day's failures. (A per-series max floated the completed line ABOVE its
  // own bar, which can't happen: completed ≤ runs, always.)
  const scaleMax = Math.max(1, ...days.map((d) => d.runs));
  const totalRuns = days.reduce((s, d) => s + d.runs, 0);
  const totalCost = days.reduce((s, d) => s + d.cost, 0);
  const activeDays = days.filter((d) => d.runs > 0).length;
  // Evenly-spaced ticks (first · a few between · last) so the axis reads as a
  // time axis, not two lonely endpoints. Capped so the labels never collide.
  const tickCount = Math.min(days.length, 5);
  const ticks =
    tickCount <= 1
      ? [0]
      : Array.from({ length: tickCount }, (_, k) =>
          Math.round((k * (days.length - 1)) / (tickCount - 1)),
        );

  return (
    <SectionCard
      title="Activity"
      aside={
        <span className="type-subtext text-muted-foreground">
          {formatNumber(totalRuns)} runs · {formatUsd(totalCost)} · {activeDays} active days
        </span>
      }
      // The legend lives in the title row, not jammed into the axis below — so the
      // axis can be a clean row of date ticks and the series read at a glance.
      action={
        <span className="flex items-center gap-3 type-subtext text-muted-foreground">
          <span className="flex items-center gap-1.5">
            {/* radius-ignore: 2×2.5 legend swatch — 1px is a proportional hairline, below the xs token by design */}
            <span className="h-2.5 w-2 rounded-[1px] bg-muted-foreground/25" /> runs
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-3 rounded-full bg-chart-2" /> completed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-3 rounded-full bg-destructive" /> failed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-3 rounded-full bg-info" /> running
          </span>
        </span>
      }
    >
      {isLoading && days.length === 0 ? (
        <Skeleton className="h-36 w-full" />
      ) : days.length === 0 ? (
        <div className="flex h-36 items-center justify-center type-body text-muted-foreground">
          No runs in this range yet.
        </div>
      ) : (
        <>
          <LineChartPlot
            days={days}
            scaleMax={scaleMax}
            selectedDay={selectedDay}
            onSelectDay={onSelectDay}
          />

          {/* X axis — a hairline baseline + evenly-spaced date ticks, so the time
              dimension is legible instead of two endpoints crowding the legend. */}
          <div className="flex items-center justify-between border-t border-border pt-1.5 type-subtext tabular-nums text-muted-foreground">
            {ticks.map((ti) => (
              <span key={ti}>{days[ti]!.day.slice(5)}</span>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}

/** The day tip body — reused by the plot's cursor-following card. */
function DayTip({ day: d }: { day: RunsByDayRowDto }) {
  const running = d.running ?? 0;
  const failed = Math.max(0, d.runs - d.completed - running);
  return (
    <>
      <div className="type-subtext font-medium text-foreground">{formatDayMonth(d.day)}</div>
      <div className="mt-0.5 type-subtext tabular-nums text-muted-foreground">
        {d.runs} runs · <span className="text-success">{d.completed} completed</span>
        {failed > 0 ? (
          <>
            {" · "}
            <span className="text-danger">{failed} failed</span>
          </>
        ) : null}
        {running > 0 ? (
          <>
            {" · "}
            <span className="text-info">{running} running</span>
          </>
        ) : null}
        {" · "}
        {formatUsd(d.cost)}
      </div>
      <div className="mt-0.5 type-subtext text-muted-foreground">Click to see the day&apos;s runs</div>
    </>
  );
}

/** The Activity plot as a multi-series line chart: completed · failed · running,
 *  each a line on the same COUNT axis so trends read directly and nothing floats.
 *
 *  Coordinate trick: the SVG viewBox is 100 wide × 144 tall (== the h-36 track in
 *  px) and stretched to fill (x → width, y → the fixed height 1:1), so a point's x
 *  is a percentage and its y is a pixel. That lets the HTML dot/guide overlay share
 *  the EXACT same coordinates without measuring the container, and the lines stay
 *  hairline-thin under the stretch via `vectorEffect="non-scaling-stroke"`. */
function LineChartPlot({
  days,
  scaleMax,
  selectedDay,
  onSelectDay,
}: {
  days: RunsByDayRowDto[];
  scaleMax: number;
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
}) {
  const VB_H = 144; // matches h-36 (px), so viewBox y is 1:1 with the overlay px
  const PAD_T = 12;
  const PAD_B = 6;
  const n = days.length;
  const xAt = (i: number) => (n <= 1 ? 50 : (i / (n - 1)) * 100);
  const yAt = (v: number) =>
    VB_H - PAD_B - (scaleMax > 0 ? v / scaleMax : 0) * (VB_H - PAD_T - PAD_B);
  const baseY = VB_H - PAD_B; // the plot floor, where every bar/line grounds
  // Slim background bars = total runs that day, a soft backdrop the colored lines
  // sit within. viewBox-x units (0–100), stretched to the container by
  // preserveAspectRatio="none"; end bars are clamped fully into view rather than
  // half-clipped off the edge. Square corners on purpose — the non-uniform x
  // stretch would smear a rounded rx into a lopsided curve.
  const barW = n > 1 ? Math.min(6, (100 / n) * 0.62) : 20;
  const barX = (i: number) => Math.max(0, Math.min(100 - barW, xAt(i) - barW / 2));
  const runningOf = (d: RunsByDayRowDto) => d.running ?? 0;
  const failedOf = (d: RunsByDayRowDto) => Math.max(0, d.runs - d.completed - runningOf(d));
  const SERIES: { key: string; color: string; get: (d: RunsByDayRowDto) => number }[] = [
    { key: "completed", color: "var(--chart-2)", get: (d) => d.completed },
    { key: "failed", color: "var(--destructive)", get: failedOf },
    { key: "running", color: "var(--info)", get: runningOf },
  ];
  const pts = (get: (d: RunsByDayRowDto) => number) =>
    days.map((d, i) => `${xAt(i)},${yAt(get(d))}`).join(" ");

  const plotRef = React.useRef<HTMLDivElement>(null);
  const [hover, setHover] = React.useState<{ i: number; x: number; y: number } | null>(null);
  // Linger through a short fade-out so the card + guide sink away instead of
  // blinking — the same exit the Pulse tip animates.
  const [shown, setShown] = React.useState<{
    i: number;
    x: number;
    y: number;
    open: boolean;
  } | null>(null);
  React.useEffect(() => {
    if (hover) {
      setShown({ ...hover, open: true });
      return;
    }
    setShown((p) => (p && p.open ? { ...p, open: false } : p));
    const t = window.setTimeout(() => setShown(null), 160);
    return () => window.clearTimeout(t);
  }, [hover]);

  function onMove(e: React.MouseEvent) {
    const el = plotRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const rel = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
    const i = Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1))));
    setHover({ i, x: e.clientX, y: e.clientY });
  }

  const selIdx = selectedDay ? days.findIndex((d) => d.day === selectedDay) : -1;
  const active = shown ? shown.i : selIdx; // the marked day: hovered wins, else selected
  const activeDay = active >= 0 ? days[active] : null;

  return (
    <>
      <div
        ref={plotRef}
        className="relative h-36 w-full cursor-pointer"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onClick={() => {
          const i = hover?.i ?? shown?.i ?? -1;
          if (i >= 0) onSelectDay(days[i]!.day);
        }}
      >
        {/* chart-ignore: multi-series day histogram (runs bars + completed area + failed/running lines) — MetricChart is single-curve reading-vs-reference, not this plot */}
        <svg
          className="absolute inset-0 h-full w-full overflow-visible"
          viewBox={`0 0 100 ${VB_H}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="runs-completed-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--chart-2)" stopOpacity="0.16" />
              <stop offset="100%" stopColor="var(--chart-2)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Total-runs backdrop: one slim gray bar per day, drawn first so it
              sits behind the area fill and every line. */}
          {days.map((d, i) =>
            d.runs > 0 ? (
              <rect
                key={`bar-${d.day}`}
                x={barX(i)}
                y={yAt(d.runs)}
                width={barW}
                height={Math.max(0, baseY - yAt(d.runs))}
                fill="var(--muted-foreground)"
                fillOpacity={0.14}
              />
            ) : null,
          )}
          {/* A faint fill under the completed line for depth. */}
          <polygon
            points={`${xAt(0)},${VB_H} ${pts((d) => d.completed)} ${xAt(n - 1)},${VB_H}`}
            fill="url(#runs-completed-area)"
          />
          {SERIES.map((s) => (
            <polyline
              key={s.key}
              points={pts(s.get)}
              fill="none"
              stroke={s.color}
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        {/* Persistent mark for a selected day (when you're hovering elsewhere). */}
        {selIdx >= 0 && active !== selIdx && (
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-primary/40"
            style={{ left: `${xAt(selIdx)}%` }}
          />
        )}

        {/* The active (hovered, else selected) day: a vertical guide + a dot per
            series, sitting exactly on each line. Dots are HTML so they stay round
            under the SVG's horizontal stretch. */}
        {activeDay && (
          <div
            className={cn(
              "pointer-events-none absolute inset-0 transition-opacity duration-150",
              shown?.open === false && "opacity-0",
            )}
          >
            <div
              className="absolute inset-y-0 w-px bg-foreground/25"
              style={{ left: `${xAt(active)}%` }}
            />
            {SERIES.map((s) => {
              const v = s.get(activeDay);
              if (v <= 0 && s.key !== "completed") return null;
              return (
                <span
                  key={s.key}
                  className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
                  style={{ left: `${xAt(active)}%`, top: `${yAt(v)}px`, background: s.color }}
                />
              );
            })}
          </div>
        )}
      </div>

      {shown && days[shown.i] && (
        <GraphTip x={shown.x} y={shown.y} open={shown.open}>
          <DayTip day={days[shown.i]!} />
        </GraphTip>
      )}
    </>
  );
}
